import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Container,
  Divider,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MedicalServices as MedicalIcon,
  History as HistoryIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { MedicalHistory, CreateMedicalHistoryRequest, UpdateMedicalHistoryRequest, MedicalDocument } from '../types/MedicalHistory';
import { Patient } from '../types/Patient';
import { medicalHistoryService } from '../services/medicalHistoryService';
import { patientService } from '../services/patientService';
import { formatCurrency, formatDate } from '../utils/formatters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`medical-tabpanel-${index}`}
      aria-labelledby={`medical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MedicalHistoryPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [medicalHistories, setMedicalHistories] = useState<MedicalHistory[]>([]);
  const [recentHistories, setRecentHistories] = useState<MedicalHistory[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingHistory, setEditingHistory] = useState<MedicalHistory | null>(null);
  const [formData, setFormData] = useState<CreateMedicalHistoryRequest>({
    patientId: 0,
    visitDate: '',
    doctorName: 'Puneet Bharadwaj',
    department: 'General Medicine',
    diagnosis: '',
    symptoms: '',
    treatment: '',
    prescription: '',
    doctorNotes: '',
    testsOrdered: '',
    testResults: '',
    followUpInstructions: '',
    nextVisitDate: '',
    visitType: '',
    totalCost: 0,
  });

  // Document management state
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedHistoryForDocs, setSelectedHistoryForDocs] = useState<MedicalHistory | null>(null);
  const [documents, setDocuments] = useState<MedicalDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileDescription, setFileDescription] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContentType, setPreviewContentType] = useState('');

  const departments = ['Cardiology', 'Neurology', 'Pediatrics', 'Orthopedics', 'Emergency', 'General Medicine', 'Dermatology', 'Psychiatry'];
  const visitTypes = ['Consultation', 'Follow-up', 'Emergency', 'Routine Check-up', 'Surgery', 'Test/Lab', 'Vaccination'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [historiesData, patientsData] = await Promise.all([
        medicalHistoryService.getAllMedicalHistories(),
        patientService.getAllPatients(),
      ]);
      setMedicalHistories(historiesData);
      setRecentHistories(historiesData.slice(0, 10));
      setPatients(patientsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load medical histories');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status?.toLowerCase()) {
      case 'active': return 'success';
      case 'completed': return 'primary';
      case 'follow-up': return 'warning';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  const handleOpenDialog = (history?: MedicalHistory) => {
    if (history) {
      setEditingHistory(history);
      setFormData({
        patientId: history.patientId,
        visitDate: history.visitDate ? history.visitDate.split('T')[0] : '',
        doctorName: history.doctorName,
        department: history.department,
        diagnosis: history.diagnosis,
        symptoms: history.symptoms,
        treatment: history.treatment,
        prescription: history.prescription,
        doctorNotes: history.doctorNotes,
        testsOrdered: history.testsOrdered,
        testResults: history.testResults,
        followUpInstructions: history.followUpInstructions,
        nextVisitDate: history.nextVisitDate?.split('T')[0] || '',
        visitType: history.visitType,
        totalCost: history.totalCost || 0,
      });
    } else {
      setEditingHistory(null);
      setFormData({
        patientId: 0,
        visitDate: new Date().toISOString().split('T')[0],
        doctorName: 'Puneet Bharadwaj',
        department: 'General Medicine',
        diagnosis: '',
        symptoms: '',
        treatment: '',
        prescription: '',
        doctorNotes: '',
        testsOrdered: '',
        testResults: '',
        followUpInstructions: '',
        nextVisitDate: '',
        visitType: '',
        totalCost: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      if (editingHistory) {
        const updateData: UpdateMedicalHistoryRequest = {
          visitDate: formData.visitDate,
          doctorName: formData.doctorName,
          department: formData.department,
          diagnosis: formData.diagnosis,
          symptoms: formData.symptoms,
          treatment: formData.treatment,
          prescription: formData.prescription,
          doctorNotes: formData.doctorNotes,
          testsOrdered: formData.testsOrdered,
          testResults: formData.testResults,
          followUpInstructions: formData.followUpInstructions,
          nextVisitDate: formData.nextVisitDate,
          visitType: formData.visitType,
          totalCost: formData.totalCost,
          status: editingHistory.status || 'Active',
        };
        await medicalHistoryService.updateMedicalHistory(editingHistory.medicalHistoryId, updateData);
      } else {
        await medicalHistoryService.createMedicalHistory(formData);
      }
      await fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save medical history:', error);
      setError('Failed to save medical history');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this medical history record?')) {
      try {
        await medicalHistoryService.deleteMedicalHistory(id);
        await fetchData();
      } catch (error) {
        console.error('Failed to delete medical history:', error);
        setError('Failed to delete medical history');
      }
    }
  };

  // Document management functions
  const handleOpenDocumentsDialog = async (history: MedicalHistory) => {
    setSelectedHistoryForDocs(history);
    setDocumentsDialogOpen(true);
    setLoadingDocs(true);
    try {
      const docs = await medicalHistoryService.getDocuments(history.medicalHistoryId);
      setDocuments(docs);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setError('Failed to load documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleCloseDocumentsDialog = () => {
    setDocumentsDialogOpen(false);
    setSelectedHistoryForDocs(null);
    setDocuments([]);
    setSelectedFile(null);
    setFileDescription('');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPEG, PNG, GIF images and PDF files are allowed');
        return;
      }
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile || !selectedHistoryForDocs) return;

    setUploadingDoc(true);
    try {
      await medicalHistoryService.uploadDocument(
        selectedHistoryForDocs.medicalHistoryId,
        selectedFile,
        fileDescription
      );
      // Refresh documents list
      const docs = await medicalHistoryService.getDocuments(selectedHistoryForDocs.medicalHistoryId);
      setDocuments(docs);
      setSelectedFile(null);
      setFileDescription('');
      // Clear the file input
      const fileInput = document.getElementById('document-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Failed to upload document:', error);
      setError('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (doc: MedicalDocument) => {
    try {
      const blob = await medicalHistoryService.downloadDocument(doc.documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download document:', error);
      setError('Failed to download document');
    }
  };

  const handlePreviewDocument = async (doc: MedicalDocument) => {
    try {
      const blob = await medicalHistoryService.downloadDocument(doc.documentId);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewContentType(doc.contentType);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Failed to preview document:', error);
      setError('Failed to preview document');
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewOpen(false);
    setPreviewContentType('');
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await medicalHistoryService.deleteDocument(documentId);
        // Refresh documents list
        if (selectedHistoryForDocs) {
          const docs = await medicalHistoryService.getDocuments(selectedHistoryForDocs.medicalHistoryId);
          setDocuments(docs);
        }
      } catch (error) {
        console.error('Failed to delete document:', error);
        setError('Failed to delete document');
      }
    }
  };

  const getFileIcon = (contentType: string) => {
    if (contentType === 'application/pdf') {
      return <PdfIcon sx={{ color: '#dc2626' }} />;
    }
    return <ImageIcon sx={{ color: '#2C7A9C' }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderHistoryTable = (histories: MedicalHistory[]) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ 
            background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
            '& th': { 
              color: 'white',
              fontWeight: 600,
              fontSize: '0.95rem',
              borderBottom: 'none',
            }
          }}>
            <TableCell>Patient</TableCell>
            <TableCell>Visit Date</TableCell>
            <TableCell>Diagnosis</TableCell>
            <TableCell>Visit Type</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {histories.map((history, index) => (
            <TableRow 
              key={history.medicalHistoryId}
              sx={{
                backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                '&:hover': {
                  backgroundColor: '#e8f4f8',
                  transform: 'scale(1.01)',
                  transition: 'all 0.2s ease',
                },
              }}
            >
              <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                {history.patientName}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C7A9C' }}>
                  {formatDate(history.visitDate)}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 200 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={history.diagnosis}
                >
                  {history.diagnosis}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={history.visitType}
                  color="primary"
                  size="small"
                  sx={{ borderRadius: 2 }}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="View Documents">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDocumentsDialog(history)}
                      sx={{ 
                        color: '#8b5cf6',
                        '&:hover': { backgroundColor: '#f3e8ff' }
                      }}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(history)}
                      sx={{ 
                        color: '#2C7A9C',
                        '&:hover': { backgroundColor: '#e8f4f8' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(history.medicalHistoryId)}
                      sx={{ 
                        color: '#ef4444',
                        '&:hover': { backgroundColor: '#fef2f2' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {histories.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                <MedicalIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                  No medical history found
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Add the first medical history record to get started
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ 
          background: 'linear-gradient(135deg, #c5dfe8 0%, #e8f4f8 100%)',
          borderRadius: 3,
          m: 2
        }}
      >
        <CircularProgress sx={{ color: '#2C7A9C' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ 
      py: 3,
      background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
      backgroundSize: '20px 20px',
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Card 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          borderRadius: 4,
          mb: 3,
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <MedicalIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  Medical History
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                  Comprehensive patient medical records and history
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Add Medical Record
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #e8f4f8 0%, #c5dfe8 100%)',
              border: '1px solid #6AB5D1',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 40, color: '#2C7A9C', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E5A73', mb: 1 }}>
                {medicalHistories.length}
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                Total Records
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              border: '1px solid #86efac',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <HistoryIcon sx={{ fontSize: 40, color: '#059669', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#047857', mb: 1 }}>
                {medicalHistories.filter(h => 
                  new Date(h.visitDate) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                ).length}
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #fbbf24',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <HospitalIcon sx={{ fontSize: 40, color: '#d97706', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#92400e', mb: 1 }}>
                {new Set(medicalHistories.map(h => h.patientId)).size}
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b' }}>
                Unique Patients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Section */}
      <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #6AB5D1' }}>
        <CardContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by patient name, diagnosis, or doctor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#2C7A9C', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#2C7A9C',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2C7A9C',
                },
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Tabs and Content */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #6AB5D1', overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#f8fafc' }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                '&.Mui-selected': {
                  color: '#2C7A9C',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#2C7A9C',
                height: 3,
              },
            }}
          >
            <Tab label="All Records" />
            <Tab label="Recent Records" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {renderHistoryTable(medicalHistories)}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderHistoryTable(recentHistories)}
        </TabPanel>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            border: '1px solid #6AB5D1',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          fontWeight: 600,
        }}>
          {editingHistory ? 'Edit Medical Record' : 'Add New Medical Record'}
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Patient</InputLabel>
                <Select
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: Number(e.target.value) })}
                  label="Patient"
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.patientId} value={patient.patientId}>
                      {patient.fullName} - {patient.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Visit Date"
                type="date"
                value={formData.visitDate}
                onChange={(e) => setFormData({ ...formData, visitDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Visit Type</InputLabel>
                <Select
                  value={formData.visitType}
                  onChange={(e) => setFormData({ ...formData, visitType: e.target.value })}
                  label="Visit Type"
                >
                  {visitTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Diagnosis"
                value={formData.diagnosis}
                onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Symptoms"
                value={formData.symptoms}
                onChange={(e) => setFormData({ ...formData, symptoms: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Treatment"
                value={formData.treatment}
                onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prescription"
                value={formData.prescription}
                onChange={(e) => setFormData({ ...formData, prescription: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tests Ordered"
                value={formData.testsOrdered}
                onChange={(e) => setFormData({ ...formData, testsOrdered: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Test Results"
                value={formData.testResults}
                onChange={(e) => setFormData({ ...formData, testResults: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Doctor Notes"
                value={formData.doctorNotes}
                onChange={(e) => setFormData({ ...formData, doctorNotes: e.target.value })}
                multiline
                rows={3}
                placeholder="Any additional notes from the doctor..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Follow-up Instructions"
                value={formData.followUpInstructions}
                onChange={(e) => setFormData({ ...formData, followUpInstructions: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Next Visit Date"
                type="date"
                value={formData.nextVisitDate}
                onChange={(e) => setFormData({ ...formData, nextVisitDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: '#fafafa' }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              borderColor: '#cbd5e1',
              color: '#64748b',
              '&:hover': { borderColor: '#94a3b8' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
              fontWeight: 600,
            }}
          >
            {editingHistory ? 'Update' : 'Save'} Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog 
        open={documentsDialogOpen} 
        onClose={handleCloseDocumentsDialog} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            border: '1px solid #6AB5D1',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
          color: 'white',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon />
            Documents - {selectedHistoryForDocs?.patientName}
          </Box>
          <IconButton onClick={handleCloseDocumentsDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
          {/* Upload Section */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f3e8ff', borderRadius: 2, border: '1px dashed #8b5cf6' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#6d28d9', mb: 2 }}>
              Upload New Document
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{
                    borderColor: '#8b5cf6',
                    color: '#6d28d9',
                    '&:hover': { borderColor: '#6d28d9', backgroundColor: 'white' }
                  }}
                >
                  {selectedFile ? selectedFile.name : 'Choose File'}
                  <input
                    id="document-upload"
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/gif,application/pdf"
                    onChange={handleFileSelect}
                  />
                </Button>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Description (optional)"
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  onClick={handleUploadDocument}
                  disabled={!selectedFile || uploadingDoc}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  }}
                >
                  {uploadingDoc ? <CircularProgress size={20} color="inherit" /> : 'Upload'}
                </Button>
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ color: '#64748b', mt: 1, display: 'block' }}>
              Allowed: JPEG, PNG, GIF, PDF (max 10MB)
            </Typography>
          </Box>

          {/* Documents List */}
          {loadingDocs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#8b5cf6' }} />
            </Box>
          ) : documents.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AttachFileIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                No documents uploaded
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Upload your first document using the form above
              </Typography>
            </Box>
          ) : (
            <List sx={{ bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              {documents.map((doc, index) => (
                <ListItem 
                  key={doc.documentId}
                  sx={{ 
                    borderBottom: index < documents.length - 1 ? '1px solid #e2e8f0' : 'none',
                    '&:hover': { backgroundColor: '#f8fafc' }
                  }}
                >
                  <ListItemIcon>
                    {getFileIcon(doc.contentType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.fileName}
                    secondary={
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {formatFileSize(doc.fileSize)} • {formatDate(doc.uploadedDate)}
                        </Typography>
                        {doc.description && (
                          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                            {doc.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Preview">
                        <IconButton 
                          size="small" 
                          onClick={() => handlePreviewDocument(doc)}
                          sx={{ color: '#2C7A9C' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDownloadDocument(doc)}
                          sx={{ color: '#059669' }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteDocument(doc.documentId)}
                          sx={{ color: '#ef4444' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={handleClosePreview} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            height: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          Document Preview
          <IconButton onClick={handleClosePreview} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#1e293b' }}>
          {previewUrl && (
            previewContentType === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Document Preview"
              />
            ) : (
              <Box 
                component="img" 
                src={previewUrl} 
                alt="Document Preview"
                sx={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain' 
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default MedicalHistoryPage;